import { FirestoreCollections } from '@griply/types';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { newGoalConverter } from 'shared/services/backend-api/converters/goal';
import { Goal, NewGoal } from 'shared/types/goal';
import { RoleOption } from 'shared/types/roles';
import { Timestamp } from 'shared/types/timestamp';

import { dateToFirestoreTimestamp } from './converters/date-to-firestore-timestamp';
import {
  BatchDocuments,
  getAuthentication,
  getCollection,
  getDatabase,
} from './helpers';

export const createGoals = async (goals: NewGoal[]): Promise<Goal[]> => {
  // Get the currently authenticated user
  const user = (await getAuthentication()).currentUser;

  if (!user) {
    throw Error('unauthenticated');
  }

  const goalCollection = getCollection(FirestoreCollections.Goals);

  if (goals.length === 1) {
    // if only one goal is being created, we can use setDoc directly
    const docRef = doc(goalCollection);
    const data = {
      ...dateToFirestoreTimestamp(newGoalConverter(goals[0])),
      id: docRef.id,
      createdAt: dateToFirestoreTimestamp(new Date()),
      updatedAt: serverTimestamp() as unknown as Timestamp,
      completedAt: null,
      roles: { [RoleOption.Owner]: user.uid, [RoleOption.All]: [user.uid] },
    } as unknown as Goal;

    setDoc(docRef, data);

    return [data];
  }

  const batchDocuments = new BatchDocuments(getDatabase());

  const goalDocs = goals.map((goal) => {
    const docRef = doc(goalCollection);

    return {
      ref: docRef,
      data: {
        ...dateToFirestoreTimestamp(newGoalConverter(goal)),
        id: docRef.id,
        createdAt: dateToFirestoreTimestamp(new Date()),
        updatedAt: serverTimestamp() as unknown as Timestamp,
        completedAt: null,
        roles: { [RoleOption.Owner]: user.uid, [RoleOption.All]: [user.uid] },
      } as unknown as Goal,
    };
  });

  goalDocs.forEach((docToAdd) => batchDocuments.addDocument(docToAdd));

  batchDocuments.commit();

  return goalDocs.map(({ data }) => data);
};
